import { useMutation } from 'react-query'
import { toast } from 'react-toastify'
import { updateNationalCode } from 'services/auth_national'
import posthog from 'posthog-js'

/**
 * The backend answers this endpoint with English strings and Shahkar's own
 * machine codes (`account_management/views/kyc_view.py`, `utils/kyc_services.py`).
 * Every one of them used to collapse into «مشکلی پیش آمده!», which turned the
 * single most common real failure — the national code not belonging to this
 * phone number — into a mystery the user could only respond to by trying the
 * same code again.
 */
const MESSAGES: Record<string, string> = {
    NationalCode_Phone_Mismatch:
        'این کد ملی به شماره موبایل شما تعلق ندارد. لطفاً کد ملی صاحب همین شماره را وارد کنید.',
    Shahkar_Server_Error:
        'سامانه احراز هویت در دسترس نیست. لطفاً کمی بعد دوباره تلاش کنید.',
    Invalid_Token:
        'سامانه احراز هویت در دسترس نیست. لطفاً کمی بعد دوباره تلاش کنید.',
    "Field 'national_code' is required.": 'کد ملی واردشده معتبر نیست.',
    'national_code is already set and cannot be changed.':
        'کد ملی شما قبلاً ثبت شده است و قابل تغییر نیست.',
    'national_code and kyc is already set and cannot be changed.':
        'کد ملی شما قبلاً تایید شده است و قابل تغییر نیست.',
}

const messageFor = (error: unknown): string => {
    const detail = (
        error as { response?: { data?: { detail?: string } } } | undefined
    )?.response?.data?.detail
    return (detail && MESSAGES[detail]) || 'مشکلی پیش آمده!'
}

export default function useAddNationalCode(refetch: () => void) {
    const { mutate } = useMutation({
        mutationFn: (nationalCode: string) => {
            return updateNationalCode(nationalCode)
        },
        onSuccess: () => {
            // National code itself is PII — capture the conversion, not the value.
            posthog.capture('national_code_verified')
            toast.success('کد ملی با موفقیت تایید شد')
            refetch()
        },
        onError: (error) => {
            const detail = (
                error as
                    | {
                          response?: {
                              status?: number
                              data?: { detail?: string }
                          }
                      }
                    | undefined
            )?.response
            posthog.capture('national_code_verification_failed', {
                status: detail?.status,
                // The reason, never the code itself.
                reason: detail?.data?.detail,
            })
            toast.error(messageFor(error))
            // A 409 means the server already holds a code for this member; the
            // page only learns that by re-reading, and it is what flips the
            // field to its locked state.
            if (detail?.status === 409) refetch()
        },
    })
    return { mutate }
}
